/**
 * Recovers a tab left behind by a deployment.
 *
 * A deployment can retire the hashed assets of the build a tab was opened on.
 * That tab still holds the old `index.html` in memory, and its `import()` map
 * still names the old files: the next lazy route -- `RahLoginPage` above all,
 * because an expired session sends the operator back there -- fetches a file
 * that no longer exists, and Vite raises `vite:preloadError`.
 *
 * Reloading is the fix when the reload brings a newer `index.html`, which names
 * the newer assets.
 *
 * The recovery is strictly bounded, per build:
 * - The build is the entry module the page was started from, as `index.html`
 *   names it. It does not change across reloads or SSO round trips of the same
 *   build, and it does change when a newer build is served.
 * - A build remembers every failure it reloaded for, up to a fixed number, and
 *   never forgets one while it is running. A failure it already reloaded for is
 *   left to the router's error handling. A different failure in a later document
 *   -- a later deployment retiring another asset of the same build -- uses one of
 *   the remaining places. When they are all used, nothing is evicted and every
 *   further failure of that build is shown.
 * - One document asks for at most one reload, however many failures it reports,
 *   and once it has shown a failure it starts no recovery for another.
 * - A newer build starts with its own places.
 * - Without a record it can trust -- storage blocked, a write that does not
 *   stick, a value that is oversized or malformed -- nothing reloads and the
 *   error shows. A malformed value is replaced by one that closes the current
 *   build, so it cannot start a loop either.
 */
const RELOADED_URL_STORAGE_KEY = 'app:stale-chunk-reload';
const STATE_VERSION = 4;
/** Distinct failures, and so automatic reloads, one build may have in this tab. */
const MAX_FAILURES_PER_BUILD = 20;
const MAX_FAILURE_LENGTH = 300;
const MAX_BUILD_LENGTH = 256;
/** Above anything this module writes; a longer value is not its record. */
const MAX_STORED_LENGTH = 16_384;

type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem'>;

export interface StaleChunkReloadOptions {
  reload?: () => void;
  storage?: () => RecoveryStorage;
  /** The running build; read from the page's entry module when omitted. */
  build?: string;
}

interface RecoveryRecord {
  build: string;
  failures: string[];
  /** No more automatic recovery for this build. */
  closed: boolean;
}

/** Control characters would be escaped several times over in the stored JSON. */
const printable = (text: string, maxLength: number): string =>
  // eslint-disable-next-line no-control-regex
  text.replace(/[\u0000-\u001f\u007f]/g, ' ').slice(0, maxLength);

/** The entry module(s) `index.html` started the page with. */
const entryBuild = (): string => {
  try {
    const entries = Array.from(
      document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'),
      script => new URL(script.src, document.baseURI).pathname
    );
    return entries.length > 0 ? entries.join(' ') : new URL(import.meta.url).pathname;
  } catch {
    return '';
  }
};

/** Which failure this is. A payload without a string message is one unnamed failure. */
const failureOf = (event: Event): string => {
  try {
    const payload: unknown = (event as Event & { payload?: unknown }).payload;
    const message =
      typeof payload === 'object' && payload !== null
        ? (payload as { message?: unknown }).message
        : payload;
    return typeof message === 'string' ? printable(message, MAX_FAILURE_LENGTH) : '';
  } catch {
    return '';
  }
};

const isFailureList = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.length <= MAX_FAILURES_PER_BUILD &&
  value.every(failure => typeof failure === 'string' && failure.length <= MAX_FAILURE_LENGTH);

/**
 * The record for this build; `null` when the stored value cannot be trusted.
 * A value from another build, or from the version that stored a bare message,
 * starts this build afresh.
 */
const parseRecord = (raw: string | null, build: string): RecoveryRecord | null => {
  const fresh: RecoveryRecord = { build, failures: [], closed: false };
  if (raw === null) return fresh;
  if (raw.length > MAX_STORED_LENGTH) return null;
  // The previous version stored a bare failure message; JSON is never one.
  if (!/^\s*[[{]/.test(raw)) return fresh;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as { v?: unknown; build?: unknown; failures?: unknown; closed?: unknown };
  if (
    record.v !== STATE_VERSION ||
    typeof record.build !== 'string' ||
    record.build.length > MAX_BUILD_LENGTH ||
    !isFailureList(record.failures) ||
    typeof record.closed !== 'boolean'
  ) {
    return null;
  }
  if (record.build !== build) return fresh;
  return { build, failures: record.failures, closed: record.closed };
};

/** True only when the value fits, was stored, and reads back unchanged. */
const storeRecord = (storage: RecoveryStorage, record: RecoveryRecord): boolean => {
  const value = JSON.stringify({
    v: STATE_VERSION,
    build: record.build,
    failures: record.failures,
    closed: record.closed,
  });
  if (value.length > MAX_STORED_LENGTH) return false;
  try {
    storage.setItem(RELOADED_URL_STORAGE_KEY, value);
    return storage.getItem(RELOADED_URL_STORAGE_KEY) === value;
  } catch {
    return false;
  }
};

export const installStaleChunkReload = ({
  reload = () => window.location.reload(),
  storage = () => window.sessionStorage,
  build: configuredBuild,
}: StaleChunkReloadOptions = {}): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;

  const build = printable(configuredBuild ?? entryBuild(), Number.MAX_SAFE_INTEGER).slice(
    -MAX_BUILD_LENGTH
  );
  let phase: 'idle' | 'reloading' | 'surfacing' = 'idle';

  const read = (): { store: RecoveryStorage; record: RecoveryRecord | null } | undefined => {
    try {
      const store = storage();
      return { store, record: parseRecord(store.getItem(RELOADED_URL_STORAGE_KEY), build) };
    } catch {
      return undefined;
    }
  };

  const listener = (event: Event) => {
    const failure = failureOf(event);

    if (phase === 'reloading') {
      // This document is already being replaced; one reload covers every failure
      // in it. Remember them too, while there is room, so the next document of
      // this build shows them instead of reloading again.
      const state = read();
      const record = state?.record;
      if (
        state &&
        record &&
        !record.closed &&
        !record.failures.includes(failure) &&
        record.failures.length < MAX_FAILURES_PER_BUILD
      ) {
        storeRecord(state.store, { ...record, failures: [...record.failures, failure] });
      }
      event.preventDefault();
      return;
    }
    if (phase === 'surfacing') return;
    phase = 'surfacing';

    const state = read();
    if (!state) return;
    const { store, record } = state;
    if (record === null) {
      storeRecord(store, { build, failures: [], closed: true });
      return;
    }
    // Already reloaded for, or out of places: the asset is missing for a reason a
    // reload cannot fix, so let the router's error handling show it rather than loop.
    if (
      record.closed ||
      record.failures.includes(failure) ||
      record.failures.length >= MAX_FAILURES_PER_BUILD
    ) {
      return;
    }
    if (!storeRecord(store, { ...record, failures: [...record.failures, failure] })) return;

    phase = 'reloading';
    // Vite throws the error after this listener unless the event is cancelled.
    event.preventDefault();
    reload();
  };

  window.addEventListener('vite:preloadError', listener);
  return () => window.removeEventListener('vite:preloadError', listener);
};

export default installStaleChunkReload;
